// Meta's rules for the buttons of a message template:
// https://developers.facebook.com/docs/whatsapp/business-management-api/message-templates/components
export const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER', 'COPY_CODE'];
export const BUTTON_LIMITS = {
  QUICK_REPLY: 10,
  URL: 2,
  PHONE_NUMBER: 1,
  COPY_CODE: 1,
};
export const MAX_BUTTONS = 10;
export const TEXT_LIMIT = 25;
export const PHONE_LIMIT = 20;
export const URL_LIMIT = 2000;
// What the send path accepts for a coupon code (Whatsapp::PopulateTemplateParametersService).
export const CODE_LIMIT = 15;
// A link takes a single variable, and only at its very end.
export const URL_VARIABLE = '{{1}}';

const TRAILING_VARIABLE = /\{\{[^{}]*\}\}\s*$/;

export const newButton = type => ({
  type,
  text: '',
  url: '',
  dynamic: false,
  example: '',
  phoneNumber: '',
});

// A link typed or pasted with its variable ("https://site.com/order/{{1}}") becomes the fixed part of a
// link that changes per contact, which is the one form Meta accepts.
export const splitUrlVariable = url =>
  TRAILING_VARIABLE.test(url)
    ? { url: url.replace(TRAILING_VARIABLE, ''), dynamic: true }
    : { url, dynamic: false };

// The shape Captain drafts buttons in (Captain::TemplateDraftSchema), to and from the form's. A copy
// code button has no text field to fix in the editor, so whatever text the model gave it is dropped;
// only a link button has a URL, so a stray one on another type cannot make it "dynamic".
export const buttonFromDraft = button => ({
  ...newButton(button.type),
  ...(button.type === 'URL' ? splitUrlVariable(button.url || '') : {}),
  text: (button.type !== 'COPY_CODE' && button.text) || '',
  example:
    (button.type === 'COPY_CODE' ? button.code_example : button.url_example) ||
    '',
  phoneNumber: button.phone_number || '',
});

export const buttonToDraft = button => ({
  type: button.type,
  text: button.text,
  url: button.dynamic ? `${button.url}${URL_VARIABLE}` : button.url,
  url_example: button.dynamic ? button.example : '',
  phone_number: button.phoneNumber,
  code_example: button.type === 'COPY_CODE' ? button.example : '',
});

// How many buttons Meta takes, in total and of each type. The editor offers only what keeps a template
// within them, and a draft from Captain that goes over is held at the buttons step.
export const withinButtonLimits = buttons =>
  buttons.length <= MAX_BUTTONS &&
  BUTTON_TYPES.every(
    type =>
      buttons.filter(button => button.type === type).length <=
      BUTTON_LIMITS[type]
  );

export const canAddButton = (buttons, type, category) =>
  withinButtonLimits([...buttons, newButton(type)]) &&
  (type !== 'COPY_CODE' || category === 'MARKETING');

// Quick replies and the other buttons have to stay in two groups, or Meta rejects the template.
export const insertButton = (buttons, button) => {
  const at =
    button.type === 'QUICK_REPLY'
      ? buttons.filter(item => item.type === 'QUICK_REPLY').length
      : buttons.length;
  return [...buttons.slice(0, at), button, ...buttons.slice(at)];
};

export const exampleUrl = button =>
  button.dynamic ? `${button.url}${button.example}` : button.url;

// The problems worth pointing at while the field is being filled; an empty field only blocks the step.
export const buttonErrors = (button, category) => {
  const errors = {};
  if (button.text.length > TEXT_LIMIT) errors.text = 'TEXT_LENGTH';
  const url = button.url.trim();
  if (button.type === 'URL' && url && !/^https?:\/\/\S+$/.test(url)) {
    errors.url = 'URL_FORMAT';
  } else if (button.type === 'URL' && /[{}]/.test(url)) {
    errors.url = 'URL_VARIABLE';
  }
  if (button.type === 'URL' && button.dynamic && /\s/.test(button.example)) {
    errors.example = 'URL_EXAMPLE_SPACES';
  }
  const phone = button.phoneNumber.trim();
  if (button.type === 'PHONE_NUMBER' && phone && !/^\+\d+$/.test(phone)) {
    errors.phoneNumber = 'PHONE_FORMAT';
  }
  if (button.type === 'COPY_CODE' && category !== 'MARKETING') {
    errors.example = 'CODE_CATEGORY';
  } else if (
    button.type === 'COPY_CODE' &&
    button.example.length > CODE_LIMIT
  ) {
    errors.example = 'CODE_LENGTH';
  }
  return errors;
};

const REQUIRED_FIELDS = {
  QUICK_REPLY: ['text'],
  URL: ['text', 'url'],
  PHONE_NUMBER: ['text', 'phoneNumber'],
  COPY_CODE: ['example'],
};

export const isButtonComplete = (button, category) =>
  [
    ...REQUIRED_FIELDS[button.type],
    ...(button.dynamic ? ['example'] : []),
  ].every(field => button[field].trim()) &&
  !Object.keys(buttonErrors(button, category)).length;

export const buttonPayload = button => {
  const text = button.text.trim();
  if (button.type === 'QUICK_REPLY') return { type: 'QUICK_REPLY', text };
  if (button.type === 'PHONE_NUMBER') {
    return {
      type: 'PHONE_NUMBER',
      text,
      phone_number: button.phoneNumber.trim(),
    };
  }
  if (button.type === 'COPY_CODE') {
    return { type: 'COPY_CODE', example: button.example.trim() };
  }

  const url = button.url.trim();
  if (!button.dynamic) return { type: 'URL', text, url };
  // Meta keeps the example as the whole link, the way it lists approved templates.
  return {
    type: 'URL',
    text,
    url: `${url}${URL_VARIABLE}`,
    example: [`${url}${button.example.trim()}`],
  };
};
